import { Session } from "@/types/auth"
import { AuthenticationException } from "@/types/client-exceptions"
import { Timestamp } from "@/types/date"
import { StorageKeys } from "@/types/web-storage"
import nProgress from "nprogress"

import {
  resetAnalyticsSession,
  sendAnalyticsEvent,
} from "@/lib/analytics/amplitude-utils"
import { AuthV2Api } from "@/lib/auth-v2-api"
import { broadcastAuthEvent } from "@/lib/auth/broadcast-channel"
import { sessionRefreshEmitter } from "@/lib/auth/session-refresh-emitter"
import { buildClientAxios } from "@/lib/client-axios"
import { addSeconds, now, secondsBetween } from "@/lib/date-utils"
import { destroyDdb } from "@/lib/ddb"
import { EmbeddedAuthApi } from "@/lib/embedded-auth-api"
import {
  getEmbeddedAuthTokenExpiresAt,
  getEmbeddedRefreshToken,
  storeEmbeddedTokens,
} from "@/lib/embedded/token-storage"
import { isClientSide } from "@/lib/is-client-side"
import { isEmbedded } from "@/lib/is-embedded"
import { mtLocation } from "@/lib/mt-location"
import { browserRedirect } from "@/lib/url-utils"
import { webStorage } from "@/lib/web-storage"

let expiresAt: Timestamp | null = null
let disableAutoRefresh = false
let refreshPromise: Promise<void> | null = null

export function setAuthTokenExpiration(
  expiresIn: number | undefined | null
): void {
  // Only update disableAutoRefresh when we have a valid expires_in value
  // Don't change the flag on undefined to preserve previous state
  if (expiresIn) {
    if (expiresIn < 60) {
      disableAutoRefresh = true
    } else {
      disableAutoRefresh = false
    }
  }

  expiresAt = expiresIn ? addSeconds(now(), expiresIn) : null

  // For embedded mode, also store the expiration in storage
  if (isEmbedded() && expiresAt && isClientSide()) {
    webStorage.setItem(
      "embedded.mindtrip.auth_token_expires_at",
      expiresAt.epoch_millis.toString()
    )
  }
}

export async function refreshTokenIfNeeded(): Promise<void> {
  // For embedded mode, check expiration from localStorage if not in memory
  let tokenExpiresAt = expiresAt
  if (!tokenExpiresAt && isEmbedded()) {
    tokenExpiresAt = getEmbeddedAuthTokenExpiresAt()
    if (tokenExpiresAt) {
      // Store it in memory for subsequent checks
      expiresAt = tokenExpiresAt
    }
  }

  if (tokenExpiresAt && !disableAutoRefresh) {
    // need to refresh now
    const secondsToExpiration = secondsBetween(now(), tokenExpiresAt)

    if (secondsToExpiration <= 60) {
      if (!refreshPromise) {
        refreshPromise = performRefreshAuthToken()
      }

      // if it's less than 30 seconds to expiration, wait for the refresh to complete else let it continue
      if (secondsToExpiration <= 30) {
        await refreshPromise
      }
    }
  }

  return
}

export async function refreshAuthToken(
  errorCallbackUrl?: string
): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = performRefreshAuthToken(errorCallbackUrl)
  }

  return refreshPromise
}

async function performRefreshAuthToken(
  errorCallbackUrl?: string
): Promise<void> {
  try {
    if (isEmbedded()) {
      const refreshToken = getEmbeddedRefreshToken()
      if (refreshToken) {
        const embeddedAuthApi = new EmbeddedAuthApi(buildClientAxios())
        const res = await embeddedAuthApi.refresh({
          refresh_token: refreshToken,
        })
        setAuthTokenExpiration(res.auth_token.expires_in)
        storeEmbeddedTokens(res)
      } else {
        await systemSignOut({ callbackUrl: errorCallbackUrl })
        return
      }
    } else {
      const authV2Api = new AuthV2Api()
      const { session } = await authV2Api.refresh()
      setAuthTokenExpiration(session.expires_in)
      // Emit session refresh event with session data for Redux components
      sessionRefreshEmitter.emit(session)
      // Broadcast refresh event to other tabs with the new session
      broadcastAuthEvent({ type: "auth-refresh", session })
    }
  } catch (error) {
    console.error("[TokenManager] Token refresh failed:", error)
    await handleRefreshFailure(error, errorCallbackUrl)
  } finally {
    refreshPromise = null
  }
}

async function handleRefreshFailure(
  error: unknown,
  errorCallbackUrl?: string
): Promise<void> {
  if (error instanceof AuthenticationException) {
    expiresAt = null
    await systemSignOut({ callbackUrl: errorCallbackUrl })
    // We're navigating away - return a promise that never resolves
    // so callers don't continue execution or receive errors
    return new Promise(() => {})
  }
  console.error("[TokenManager] Unexpected error during token refresh:", error)
  throw error
}

async function signOutInternal(options?: {
  callbackUrl?: string | null
  embeddedAttempts?: number
  type: "user" | "system"
}): Promise<void> {
  if (!isClientSide()) {
    throw new Error("signOut can only be called on the client side")
  }

  nProgress.start()

  // Send Amplitude events before signing out
  try {
    const analyticsResult = sendAnalyticsEvent("SignOut")
    if (analyticsResult?.promise) {
      // Set 5s timeout for SignOut event
      const timeoutPromise = new Promise<void>((resolve) =>
        setTimeout(() => {
          console.error("SignOut event timeout after 5s")
          resolve()
        }, 5000)
      )
      await Promise.race([analyticsResult.promise, timeoutPromise])
    }
    resetAnalyticsSession()
  } catch (error) {
    console.error("Amplitude sign out tracking error:", error)
  }

  try {
    if (isEmbedded()) {
      // For embedded, get auth token and call embedded sign out
      const refreshToken = getEmbeddedRefreshToken()
      if (refreshToken) {
        const api = new EmbeddedAuthApi(buildClientAxios())
        await api.signOut({ refresh_token: refreshToken })
      }
    } else {
      // For web, call auth v2 sign out endpoint
      const api = new AuthV2Api()
      await api.signOut()
    }
  } catch (error) {
    console.error(`${isEmbedded() ? "Embedded" : "Web"} sign out error:`, error)
  }

  await destroyDdb()
  webStorage.clear({ type: "session" })

  const carryForwardValues: [key: StorageKeys, value: string][] = []

  // Preserve these values across sign-out:
  // - last_used_provider: for sign-in page to remember provider
  // - auth.redirect.loop.shared: loop detection counter (prevents infinite sign-out loops)
  ;(
    ["mindtrip.last_used_provider", "auth.redirect.loop.shared"] as const
  ).forEach((key) => {
    const value = webStorage.getItem(key)
    if (value) {
      carryForwardValues.push([key, value])
    }
  })

  webStorage.clear({ type: "local" })

  // Restore carry forward values
  carryForwardValues.forEach(([key, value]) => {
    webStorage.setItem(key, value)
  })

  // Broadcast sign-out event to other tabs before signing out
  broadcastAuthEvent({
    type: "auth-signout",
    signout_type: options?.type || "system",
  })

  if (isEmbedded()) {
    if (options?.embeddedAttempts) {
      webStorage.setItem(
        "embedded.mindtrip.auth.attempts",
        options.embeddedAttempts.toString()
      )
    }
    // Embedded always reloads
    mtLocation.reload()
  } else {
    const callbackUrl = options?.callbackUrl || "/"
    browserRedirect(callbackUrl)
  }
}

export async function userInitiatedSignOut(): Promise<void> {
  await signOutInternal({ callbackUrl: "/", type: "user" })
}

export async function systemSignOut(options?: {
  embeddedAttempts?: number
  callbackUrl?: string
}): Promise<void> {
  let finalCallbackUrl: string

  // If on /signOut page and no explicit callback provided, go directly to home
  if (/\/signout/i.test(mtLocation.pathname) && !options?.callbackUrl) {
    finalCallbackUrl = "/"
  } else if (options?.callbackUrl) {
    // Use provided callback URL
    finalCallbackUrl = options.callbackUrl
  } else {
    // just go home
    finalCallbackUrl = "/"
  }

  await signOutInternal({
    callbackUrl: finalCallbackUrl,
    embeddedAttempts: options?.embeddedAttempts,
    type: "system",
  })
}

export async function tryGetSession(): Promise<Session | null> {
  try {
    return (await getSession()) ?? null
  } catch (error) {
    console.error("Failed to get session:", error)
    return null
  }
}

export async function getSession(): Promise<Session> {
  if (!isClientSide()) {
    throw new Error("getSession can only be called on the client side")
  }
  const authV2Api = new AuthV2Api()
  // session endpoint returns { session: null } when unauthenticated
  const res = await authV2Api.getSession()
  return res.session
}
